<template>
    <div id="app" :class="{ 'is-logged-in': account.status.loggedIn }">
        <app-header/>
        <app-nav v-if="account.status.loggedIn"/>

        <main class="o-main">
            <transition name="fade" mode="out-in" appear>
                <router-view/>
            </transition>
            <alert/>
        </main>
    </div>
</template>

<script>
    import { mapState } from 'vuex';
    import Alert from './Alert.vue';
    import AppHeader from './app/AppHeader.vue';
    import AppNav from './app/AppNav.vue';
    import * as ls from '../helpers/localStorage';

    export default {
        components: { AppNav, Alert, AppHeader },
        computed: {
            ...mapState({
                account: (state) => state.account,
            }),
        },
        created () {
            window.addEventListener('beforeunload', this.leaving);
        },
        watch: {
            $route () {
                // clear alert on location change
                this.$store.commit('alert/clear');
            },
        },
        methods: {
            leaving () {
                // only logout the account when there is a token
                if (ls.get('account:token'))
                {
                    this.$store.dispatch('account/logout');
                }
            },
        },
    };
</script>

<style lang="scss">
    @import "../../src/assets/scss/essentials";

    html,
    body {
        overflow: hidden;
    }

    #app {
        display: grid;
        grid-template-areas: "header" "body";
        grid-template-columns: 1fr;

        &.is-logged-in {
            @include media-breakpoint-up(lg) {
                grid-template-areas: "header header" "nav body";
                grid-template-columns: rem($sidebar-width) 1fr;
            }

            /*@include media-breakpoint-up(xl) {
                grid-template-columns: rem($sidebar-width) 1fr;
            }*/
        }
    }

    .o-main {
        background-color: $gray-lightest;
        grid-area: body;
        height: calc(100vh - #{rem(map_get($header-height, xs))});
        overflow-y: auto;
        position: relative;

        @include media-breakpoint-up(sm) {
            height: calc(100vh - #{rem(map_get($header-height, sm))});
        }

        @include media-breakpoint-up(lg) {
            height: calc(100vh - #{rem(map_get($header-height, lg))});
        }
    }

    .g-container {
        grid-template-columns: grid(1) 1fr grid(1);
        grid-template-areas: ". container .";

        @include media-breakpoint-up(sm) {
            grid-template-columns: grid(2, sm) 1fr grid(2, sm);
        }

        @include media-breakpoint-up(lg) {
            grid-template-columns: grid(2, lg) 1fr grid(2, lg);
        }

        @include media-breakpoint-up(xl) {
            grid-template-columns: grid(4, lg) 1fr grid(4, lg);
        }

        &--large {
            @include media-breakpoint-up(xl) {
                grid-template-columns: grid(2, lg) 1fr grid(2, lg);
            }
        }
    }

    .o-container {
        grid-area: container;
    }

    /**
    *
    */

    .o-band {
        @each $breakpoint, $padding in $section-padding {
            @include media-breakpoint-up($breakpoint) {
                padding-bottom: rem($padding);
                padding-top: rem($padding);
            }
        }
    }

    /**
    *
    */

    .o-card {
        background-color: $white;
        border-radius: grid(1);
        display: block;
        padding: grid(2);

        @include media-breakpoint-up(lg) {
            padding: grid(1, lg);
        }

        @include media-breakpoint-up(xl) {
            padding: grid(2, lg);
        }

        &--small {
            padding: grid(1);

            @include media-breakpoint-up(lg) {
                padding: grid(1, lg);
            }
        }
    }

    /**
    *
    */

    .c-node {
        background-color: $white;
        border-radius: grid(1);
        display: flex;
        flex-direction: column;
        justify-content: center;
        padding: grid(2);
        transition: $transition-base;

        @include media-breakpoint-up(sm) {
            padding: grid(1.5, sm) grid(2, sm);
        }

        @include media-breakpoint-up(lg) {
            min-height: grid(3, lg);
        }

        &__title {
            margin-bottom: 0;
        }

        &__list {
            @include zero;
            list-style: none;
        }

        @include hover-focus {
            box-shadow: 0 rem(5) rem(30) rgba($brand-primary, .1);
        }
    }

    /**
     *
     */

    .c-link {
        color: $brand-info;
        font-weight: $font-weight-bold;
        transition: $transition-base;

        @include hover-focus {
            color: darken($brand-info, 15%);
        }

        svg,
        &__svg {
            margin-left: grid(1 / 2);
        }

        &--primary {
            color: $brand-primary;

            @include hover-focus {
                color: darken($brand-primary, 15%);
            }
        }

        &--danger {
            color: $brand-danger;

            @include hover-focus {
                color: darken($brand-danger, 15%);
            }
        }
    }

    /**
     * TRANSITION
     */

    .fade-enter-active,
    .fade-leave-active {
        transition: opacity .15s;
    }

    .fade-enter,
    .fade-leave-to {
        opacity: 0;
    }

    // btn

    .c-btn {
        @include fluid-type(13px, 16px);
        align-items: center;
        justify-self: start;
        border: rem($border-width) solid $white;
        border-radius: grid(1, sm);
        background-color: $white;
        color: $text-color;
        cursor: pointer;
        display: inline-flex;
        font-weight: $btn-font-weight;
        height: grid(1, lg);
        line-height: rem($input-btn-line-height);
        transition: $input-transition;
        padding: rem($input-btn-padding-y) rem($input-btn-padding-x);
        position: relative;
        user-select: none;
        vertical-align: middle;
        z-index: 1;

        @include media-breakpoint-down(xsl) {
            height: rem(40);
            font-size: rem(14);
            padding: rem($input-btn-padding-y) rem(18);
        }

        &.disabled,
        &[disabled] {
            opacity: 0.5;
            pointer-events: none;
        }

        @include hover {
            background-color: lighten($gray-lightest, 2%);
        }

        &:focus {
            outline: 0;
        }

        svg {
            pointer-events: none;
            margin-left: rem(10);
        }

        & + & {
            margin-left: rem(15);
        }

        &__text {
            @include media-breakpoint-down(xsl) {
                display: none;
            }
        }

        &--primary {
            background-color: $brand-primary;
            color: $white;

            @include hover {
                background-color: darken($brand-primary, 5%);
                color: $white;
            }
        }

        &--outline-primary {
            border: rem(1) solid $brand-primary;
            color: $brand-primary;

            @include hover {
                background-color: $brand-primary;
                color: $white;
            }
        }

        &--info {
            background-color: $brand-info;
            color: $white;

            @include hover {
                background-color: lighten($brand-info, 5%);
                color: $white;
            }
        }

        &--danger {
            background-color: $brand-danger;
            color: $white;

            @include hover {
                background-color: lighten($brand-danger, 5%);
                color: $white;
            }
        }

        &--tint {
            background-color: $gray-light;

            @include hover {
                background-color: lighten($gray-light, 5%);
                color: $text-color;
            }
        }

        &--success {
            background-color: $brand-success;
            color: $white;

            @include hover {
                background-color: darken($brand-success, 5%);
                color: $white;
            }
        }

        &--outline-tint {
            border: rem(1) solid $gray-light;
            color: $gray-light;
        }

        &--square {
            @include square(grid(1, lg));
            @include flex-center;
            padding: 0;

            @include media-breakpoint-down(xsl) {
                @include square(rem(40) !important);
                font-size: rem(14);
            }
        }

        &--white {
            background-color: $white;
            color: $brand-info;

            @include hover {
                background-color: $gray-light;
                color: $text-color;
            }
        }
    }

    .c-btn-group {
        align-items: center;
        display: flex;
        height: inherit;

        &--stacked {
            .c-btn {
                border: none;
                margin-left: 0;
                width: grid(1.25, lg);

                svg {
                    margin: 0;
                }

                &:first-child {
                    border-top-right-radius: 0;
                    border-bottom-right-radius: 0;

                    svg {
                        margin-right: rem(-4);
                    }
                }

                &:last-child {
                    border-top-left-radius: 0;
                    border-bottom-left-radius: 0;

                    svg {
                        margin-left: rem(-4);
                    }
                }
            }
        }
    }

    //

    .btn-complete-item {
        transition: all .3s;
    }

    .btn-complete-enter,
    .btn-complete-leave-to {
        opacity: 0;
        transform: scale(0.5);
    }

    .btn-complete-leave-active {
        position: absolute;
    }

    .btn-move {
        transition: transform .3s;
    }

    /**
     * LOADER
     */

    .c-loading {
        align-items: center;
        display: flex;
        padding: rem(10) rem(20);
        justify-content: center;

        .spinner {
            display: inline-block;
            margin-right: rem(10);
        }
    }

    /**
     * TABLE
     */

    .table {
        border-radius: grid(1);
        display: grid;
        grid-template-columns: rem(100) 1fr;
        overflow: hidden;

        /*@include media-breakpoint-only(sm) {
            margin: 0 auto;
            max-width: 75%;
        }*/

        @include media-breakpoint-up(md) {
            grid-template-columns: 1fr;
        }

        &__thead {

        }

        &__tbody {
            background: $gray-light;
        }

        &__tr {
            background-color: $white;
            display: grid;
            grid-template-columns: 1fr;

            & + & {
                border-top: rem(1) solid $gray-light;
            }

            &.is-selected {
                outline: rem(1) solid $brand-success;
            }

            .table__thead & {
                @include media-breakpoint-up(md) {
                    display: none;

                    &:first-child {
                        display: grid;
                    }
                }
            }

            &--hover {
                transition: $transition-base;

                @include hover-focus {
                    background-color: $gray-light;
                    // color: $brand-danger;
                }
            }
        }

        &__td,
        &__th {
            padding: grid(1) grid(1, sm);

            .table--small & {
                padding: grid(0.7) grid(1);
            }

            & + & {
                @include media-breakpoint-up(lg) {
                    border-left: rem(1) solid $gray-light;
                }
            }

            @include media-breakpoint-down(xsl) {
                min-height: rem(50);
                overflow: hidden;
                width: 100%;
                white-space: nowrap;
                text-overflow: ellipsis;
            }

            @include media-breakpoint-only(sm) {
                min-height: rem(55);
            }
        }

        &__td {
            font-size: rem(16);

            .table--small & {
                font-size: rem(14);
            }
        }

        &__th {
            background-color: $brand-primary;
            color: $white;
            font-weight: $font-weight-bold;
            font-size: rem(18);

            .table--small & {
                font-size: rem(14);
            }

            &--order {
                cursor: pointer;
                transition: $transition-base;

                @include hover-focus {
                    color: $gray-base;
                }
            }
        }

        &--small {
            font-size: rem(14);
        }

        &--full-width {
            .table__th {
                grid-column: 1 / -1;
            }
        }
    }

    /**
    * TABS
    */

    .vue-tabs {

    }

    .vue-tablist {
        @include zero;
        list-style: none;
        display: flex;
        width: 100%;

        @include media-breakpoint-down(xsl) {
            display: block;
        }
    }

    .vue-tab {
        @include flex-center;
        cursor: pointer;
        color: $brand-primary;
        border-bottom: rem(1) solid $border-color;
        border-top-left-radius: grid(1);
        border-top-right-radius: grid(1);
        height: rem(100);
        flex: 1 1 auto;
        font-weight: $font-weight-bold;
        position: relative;
        transition: $transition-base;
        z-index: 1;

        @include media-breakpoint-down(xsl) {
            border-radius: grid(1);
            height: rem(60);
        }

        @include media-breakpoint-up(sm) {
            &:before {
                @include absolute(top calc(100% + #{rem(1)}) left 0 right 0 bottom grid(-1, lg));
                content: "";
                background-color: $white;
                opacity: 0;
                transition: $transition-base;
                z-index: -1;
            }
        }

        @include hover-focus {
            color: $brand-danger;
        }

        &[aria-selected="true"] {
            background-color: $white;
            box-shadow: 0 rem(5) rem(30) rgba($brand-primary, .1);
            color: $brand-danger;

            &:before {
                opacity: 1;
            }
        }
    }

    .vue-tabpanel {
        background-color: $white;
        border-bottom-left-radius: grid(1);
        border-bottom-right-radius: grid(1);
        box-shadow: 0 rem(5) rem(30) rgba($brand-primary, .1);
        padding: rem(30) 0;

        @include media-breakpoint-up(sm) {
            padding: grid(2, sm);
        }

        @include media-breakpoint-up(lg) {
            padding: grid(1, lg);
        }

        @include media-breakpoint-up(xxl) {
            padding: grid(2, lg);
        }
    }

    /**
     * FORMS
     */

    .form {
        display: grid;
        grid-gap: grid(2) 0;

        @include media-breakpoint-up(sm) {
            grid-gap: grid(2, sm) 0;
        }

        &-fieldset {
            display: grid;
            grid-gap: grid(1, sm) 0;
        }

        &-group {
            position: relative;
        }

        &-loader {
            @include absolute(top rem(13) right rem(15));
            z-index: 1;
        }

        &-checkboxes {
            display: grid;
            grid-gap: grid(1) 0;
        }
    }

    .form-submit-btn {
        justify-self: flex-end;
    }

    .custom-control-name {
        @include fluid-type(15px, 18px);
        align-self: flex-end;
        color: $brand-primary;
        display: block;
        padding-left: rem(40);
        margin-left: grid(1, sm);
        position: relative;

        &--reset {
            margin-left: 0;
        }

        // bg
        &::before {
            @include square(rem(20));
            @include absolute(top 50% left 0);
            display: block;
            pointer-events: none;
            content: '';
            user-select: none;
            background-color: transparent;
            border: rem(1) solid $brand-danger;
            border-radius: rem(5);
            transition: all .3s $transition-timing-base;
            transform: translateY(-50%);
        }

        // icon
        &::after {
            @include square(rem(18));
            @include absolute(top rem(1.5) left rem(1));
            background: transparent url('/assets/images/check.svg') center center no-repeat;
            display: block;
            content: '';
            transition: transform .3s $transition-timing-base;
            transform: scale(0);

            @include media-breakpoint-down(xsl) {
                top: rem(8);
            }
        }
    }

    .custom-control-label {
        cursor: pointer;
        display: flex;
        line-height: 1.2;
        margin-bottom: 0;
        text-align: left;

        .is-disabled & {
            cursor: not-allowed;
            opacity: 0.4;
        }
    }

    .custom-control-input {
        position: absolute;
        z-index: -1; // Put the input behind the label so it doesn't overlay text
        opacity: 0;

        &:checked ~ .custom-control-name:before {
            border-color: $brand-danger;
            background-color: $white !important;
        }

        &:checked ~ .custom-control-name:after {
            transform: scale(1);
        }

        &:active ~ .custom-control-name:before {
            color: $brand-danger;
            background-color: $white;
        }

        &:disabled {
            ~ .custom-control-name {
                color: $gray-light;

                &::before {
                    background-color: $gray-light;
                    border-color: $gray-light;
                }
            }
        }
    }

    /**
     * MODAL
     */
    .sweet-modal {
        background-color: $gray-lighter;
        box-shadow: none;
        border: rem(1) solid $gray-light;
        border-radius: 0;

        @include media-breakpoint-up(xl) {
            max-width: none;
            width: 50%;
        }

        .form {
            @include media-breakpoint-up(xl) {
                max-width: 75%;
            }
        }

        .sweet-content {
            padding: rem(30);
        }

        .sweet-box-actions {
            top: 0;
            right: 0;

            .sweet-action-close {
                @include flex-center;
                background-color: $brand-primary;
                border-radius: 0;
                transition: $transition-base;

                @include media-breakpoint-up(sm) {
                    @include square(rem(50));
                }

                svg {
                    path {
                        transition: $transition-base;
                        fill: $white;
                    }
                }

                @include hover {
                    background-color: darken($brand-primary, 5%);

                    svg {
                        path {
                            fill: $white;
                        }
                    }
                }
            }
        }
    }

    /**
     * DIALOG
     */

    .dg-main-content {
        padding: grid(1, lg) !important;
        max-width: rem(500) !important;
    }

    .dg-content {
        font-size: rem(18) !important;
    }

    .dg-content-body {
        padding-bottom: grid(1, lg) !important;
        border-bottom:  none !important;
    }

    .dg-content-footer {
        padding-top: 0 !important;
    }

    .dg-btn {
        border-radius: grid(1) !important;
        border-width: 0 !important;
        border-color: $brand-info !important;
        height: rem($input-btn-height) !important;
        line-height: rem($input-btn-line-height) !important;
        transition: $input-transition !important;
        padding: rem($input-btn-padding-y) rem($input-btn-padding-x) !important;

        &--cancel {
            background-color: $brand-danger !important;
        }

        &--ok {
            background-color: $brand-info !important;
            color: $white !important;
        }
    }

    /**
     *
     */

    [class^="g-"] {
        display: grid;
    }
</style>
